import SavePoint from "@/utils/savePoint";
import {
  beginCreateAuthHandoff,
  clearCreateAuthHandoff,
  createCreateAuthIntentId,
  hasCreateAuthHandoff,
} from "@/utils/createAuthHandoffPersistence";
import { clearCreditPurchaseAfterAuth } from "@/utils/creditAuthIntentPersistence";
import { clearQuoteResumeAfterAuth } from "@/utils/quoteResumePersistence";

const ACTIVE_HANDOFF_KEY = "create.auth.activeHandoff.v1";

function clearHandoffArtifacts(handoffId: string): void {
  clearCreateAuthHandoff(handoffId);
  clearCreditPurchaseAfterAuth(handoffId);
  clearQuoteResumeAfterAuth(handoffId);
  SavePoint.clear(handoffId);
}

/**
 * Starts an auth handoff and replaces only this tab's abandoned predecessor.
 * Other tabs have separate sessionStorage and retain their own exact intents.
 */
export function beginTrackedCreateAuthHandoff(
  sourceUserId?: string | null,
  options: { allowUnowned?: boolean } = {}
): string | null {
  const handoffId = sourceUserId
    ? beginCreateAuthHandoff(sourceUserId)
    : options.allowUnowned
      ? createCreateAuthIntentId()
      : null;
  if (!handoffId || typeof window === "undefined") return handoffId;

  try {
    const abandonedHandoffId =
      window.sessionStorage.getItem(ACTIVE_HANDOFF_KEY);
    window.sessionStorage.setItem(ACTIVE_HANDOFF_KEY, handoffId);
    if (
      abandonedHandoffId &&
      abandonedHandoffId !== handoffId &&
      (options.allowUnowned ||
        hasCreateAuthHandoff(abandonedHandoffId, sourceUserId))
    ) {
      clearHandoffArtifacts(abandonedHandoffId);
    }
  } catch {
    // The new durable handoff remains usable even if per-tab tracking is
    // unavailable. All handoff records still expire after the hard TTL.
  }

  return handoffId;
}

function releaseActiveMarker(handoffId: string): void {
  if (typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(ACTIVE_HANDOFF_KEY) === handoffId) {
      window.sessionStorage.removeItem(ACTIVE_HANDOFF_KEY);
    }
  } catch {
    // Durable cleanup is independent from this per-tab hint.
  }
}

/**
 * Complete account adoption without consuming the quote/credit action that
 * resumes immediately afterward.
 */
export function clearTrackedCreateAuthHandoff(handoffId?: string | null): void {
  if (!handoffId) return;
  clearCreateAuthHandoff(handoffId);
  releaseActiveMarker(handoffId);
}

/** Cancel an exact handoff and every action/savepoint scoped to it. */
export function cancelTrackedCreateAuthHandoff(
  handoffId?: string | null
): void {
  if (!handoffId) return;
  clearHandoffArtifacts(handoffId);
  releaseActiveMarker(handoffId);
}

export function clearActiveCreateAuthHandoffMarker(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(ACTIVE_HANDOFF_KEY);
  } catch {
    // Best effort at a real identity boundary.
  }
}

/** Cancel only the handoff initiated by this tab, leaving sibling tabs intact. */
export function cancelActiveCreateAuthHandoff(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const handoffId = window.sessionStorage.getItem(ACTIVE_HANDOFF_KEY);
    if (!handoffId) return null;
    cancelTrackedCreateAuthHandoff(handoffId);
    return handoffId;
  } catch {
    return null;
  }
}
